<script lang="ts">
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	import {
		SidepanelLayout,
		SidepanelContent,
		Button,
		Text,
		Stack,
		ParameterCard,
		Toggle,
		toast,
	} from '@Shelly/shelly-design-system';
	import { scenes } from 'src/stores/lists/scenes';
	import { rooms } from 'src/stores';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { _deepClone } from 'src/helpers/utilities';
	import { postReq } from 'src/services/http';
	import { setSidePanelState } from '../../sidepanel-helpers';
	import type { ISceneObj } from 'src/types/SceneType';
	import type { InputClickEvent } from 'src/types/InputClickEvent';
	import { isFlow } from 'src/components/Lists/list-helpers';
	import { collectSceneFids } from './flow-sidepanel-helpers';
	import { mobile } from 'src/services/mobile-rework';

	const FLOWS_BASE = '/home/flows';

	let savingMeta = false;

	$: id = $sidePanelStateCombined?.id;
	$: scene = $scenes[id] ? (_deepClone($scenes[id]) as ISceneObj) : undefined;
	$: name = scene?._meta?.name?.trim() || $format('label_unnamed_flow', { default: 'Unnamed flow' });
	$: roomName = scene?._meta?.roomn || '';

	$: flowGraph = scene?._meta?.flowGraph as
		| { nodes?: unknown[]; edges?: unknown[]; savedAt?: number }
		| undefined;
	$: sceneFids = collectSceneFids(scene);
	$: nodeCount = Array.isArray(flowGraph?.nodes) ? (flowGraph!.nodes as any[]).length : sceneFids.size;
	$: edgeCount = Array.isArray(flowGraph?.edges) ? (flowGraph!.edges as any[]).length : 0;
	$: savedAtLabel = (() => {
		const t = flowGraph?.savedAt;
		if (typeof t !== 'number' || !Number.isFinite(t)) return '';
		try {
			return new Date(t).toLocaleString(undefined, {
				dateStyle: 'medium',
				timeStyle: 'short',
			});
		} catch {
			return '';
		}
	})();

	// Two-way binding for toggles — kept in sync with `scene` when store updates
	let enabled = false;
	let runOnIngest = false;
	let isMobile = mobile.isMobile;
	$: if (scene) {
		enabled = !!scene._enabled;
		runOnIngest = !!scene._run_on_ingest;
	}

	function goToFlowEditor() {
		if (!scene?._id && !scene?.id) return;
		const fid = String(scene._id || scene.id);
		push(`${FLOWS_BASE}/${fid}/edit`);
		setSidePanelState({ open: false });
	}

	async function persistScenePatch(patch: Partial<ISceneObj>) {
		if (!scene?._id || savingMeta) return;
		savingMeta = true;
		const next = _deepClone(scene) as ISceneObj;
		if (patch._enabled !== undefined) next._enabled = patch._enabled;
		if (patch._run_on_ingest !== undefined) next._run_on_ingest = patch._run_on_ingest;
		next._meta = { ...next._meta };
		next._meta.sceneType = 'flow';
		next._meta.isFlow = true;
		next._meta.room = next._meta.room == '' ? -1 : next._meta.room;
		const roomEntry = $rooms[next._meta.room];
		if (typeof roomEntry !== 'undefined') {
			next._meta.roomn = roomEntry.name;
		} else if (next._meta.room == -1) {
			next._meta.roomn = 'General';
		} else {
			next._meta.roomn = 'Missing';
		}

		try {
			const res = await postReq('/scene/edit', {
				id: next._id,
				scene_script: JSON.stringify(next),
			});
			if (res?.isok) {
				scenes.editScene(next);
				toast.open({
					type: 'success',
					title: $format('label_saved', { default: 'Saved' }),
				});
			} else {
				throw new Error(res?.error || 'save failed');
			}
		} catch (e) {
			console.error(e);
			toast.open({
				type: 'danger',
				title: $format('error_save_failed', { default: 'Save failed' }),
			});
			if (scene) {
				enabled = !!scene._enabled;
				runOnIngest = !!scene._run_on_ingest;
			}
		} finally {
			savingMeta = false;
		}
	}

	function onEnabledToggle(e: InputClickEvent) {
		enabled = e.target.checked;
		persistScenePatch({ _enabled: enabled });
	}

	function onRunOnIngestToggle(e: InputClickEvent) {
		runOnIngest = e.target.checked;
		persistScenePatch({ _run_on_ingest: runOnIngest });
	}

	function openClassicSceneInspector() {
		if (!id || id === '-1') return;
		setSidePanelState({ type: 'scene', id, open: true });
	}
</script>

{#if typeof scene === 'object' && isFlow(scene)}
	<SidepanelLayout
		title={name}
		subtitle={roomName}
		settingsIcon={!isSubAccount() ? 'fat fa-diagram-project' : undefined}
		onSettings={!isSubAccount() ? goToFlowEditor : undefined}
	>
		<SidepanelContent>
			<Stack gap="m">
				<Button
					variant="primary"
					class="width-full"
					disabled={isMobile}
					on:click={goToFlowEditor}
					aria-label={$format('flow_edit_open_editor', { default: 'Edit flow in editor' })}
				>
					{$format('flow_edit_in_editor', { default: 'Edit flow' })}
				</Button>

				<div class="flow-meta-toggles">
					<Toggle
						id="flow-side-enabled"
						size="m"
						checked={enabled}
						disabled={savingMeta || isSubAccount()}
						aria-label={$format('scene_enebled_msg', { default: 'Enabled scene' })}
						on:click={onEnabledToggle}
					>
						{$format('scene_enebled_msg', { default: 'Enabled' })}
					</Toggle>
					<Toggle
						id="flow-side-run-ingest"
						size="m"
						checked={runOnIngest}
						disabled={savingMeta || isSubAccount()}
						aria-label={$format('run_on_save', { default: 'Run scene on save' })}
						on:click={onRunOnIngestToggle}
					>
						{$format('run_on_save', { default: 'Run on save' })}
					</Toggle>
				</div>

				<Text styleType="body-extra-small" class="text-color-muted">
					{$format('flow_sidepanel_summary_heading', { default: 'Flow graph' })}
				</Text>
				<ParameterCard
					icon="fas fa-circle-nodes"
					title={$format('flow_sidepanel_nodes_edges', {
						default: '{nodes} nodes · {edges} connections',
						values: { nodes: String(nodeCount), edges: String(edgeCount) },
					})}
					description={savedAtLabel
						? $format('flow_sidepanel_last_saved', {
								default: 'Last saved: {when}',
								values: { when: savedAtLabel },
							})
						: $format('flow_sidepanel_not_saved_yet', {
								default: 'Open the editor to build or change this flow.',
							})}
					size="m"
				/>

				{#if !isSubAccount()}
					<hr class="mt-m mb-s" />
					<Button
						variant="secondary"
						class="width-full"
						size="s"
						on:click={openClassicSceneInspector}
					>
						{$format('flow_open_classic_scene_panel', {
							default: 'Classic scene details',
						})}
					</Button>
					<Text styleType="body-small" class="text-color-muted">
						{$format('flow_open_classic_scene_hint', {
							default: 'Full when/do/else previews and duplicate tools for advanced use.',
						})}
					</Text>
				{/if}
			</Stack>
		</SidepanelContent>
	</SidepanelLayout>
{:else if id !== '-1'}
	<SidepanelLayout title={$format('flow_not_flow_title', { default: 'Not a flow' })}>
		<SidepanelContent>
			<ParameterCard
				icon="fas fa-diagram-project"
				title={$format('label_not_found_el', { default: 'Not found or not a flow' })}
				description={'ID: ' + id}
				size="m"
			/>
		</SidepanelContent>
	</SidepanelLayout>
{/if}

<style>
	.flow-meta-toggles {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s, 0.75rem);
	}
</style>
